import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'

export interface Vote {
  id: string
  userId: string
  targetId: string
  targetType: 'thread' | 'post' | 'comment'
  type: 'upvote' | 'downvote'
  timestamp: Date
}

interface VoteSystemProps {
  targetId: string
  targetType: 'thread' | 'post' | 'comment'
  initialUpvotes: number
  initialDownvotes: number
  userVote?: 'upvote' | 'downvote' | null
  onVote: (type: 'upvote' | 'downvote') => void
  onRemoveVote: () => void
  orientation?: 'vertical' | 'horizontal'
  size?: 'small' | 'medium' | 'large'
  showScore?: boolean
  animated?: boolean
}

export default function VoteSystem({
  targetId,
  targetType,
  initialUpvotes,
  initialDownvotes,
  userVote,
  onVote,
  onRemoveVote,
  orientation = 'vertical',
  size = 'medium',
  showScore = true,
  animated = true
}: VoteSystemProps) {
  const { isLoggedIn } = useAuth()
  const [upvotes, setUpvotes] = useState(initialUpvotes)
  const [downvotes, setDownvotes] = useState(initialDownvotes)
  const [currentUserVote, setCurrentUserVote] = useState(userVote)
  const [isAnimating, setIsAnimating] = useState(false)
  const [lastVoteTime, setLastVoteTime] = useState<Date | null>(null)

  const score = upvotes - downvotes

  // Update local state when props change
  useEffect(() => {
    setUpvotes(initialUpvotes)
    setDownvotes(initialDownvotes)
    setCurrentUserVote(userVote)
  }, [initialUpvotes, initialDownvotes, userVote])

  // Animate score changes
  useEffect(() => {
    if (animated && lastVoteTime) {
      setIsAnimating(true)
      const timer = setTimeout(() => setIsAnimating(false), 300)
      return () => clearTimeout(timer)
    }
  }, [upvotes, downvotes, animated, lastVoteTime])

  const handleVote = async (type: 'upvote' | 'downvote') => {
    if (!isLoggedIn) {
      // TODO: Show login prompt or modal
      return
    }

    const previousVote = currentUserVote
    const timestamp = new Date()
    setLastVoteTime(timestamp)

    if (currentUserVote === type) {
      // Remove vote
      setCurrentUserVote(null)
      if (type === 'upvote') {
        setUpvotes(prev => prev - 1)
      } else {
        setDownvotes(prev => prev - 1)
      }
      onRemoveVote()
    } else {
      // Add or change vote
      setCurrentUserVote(type)
      
      if (previousVote) {
        // Changing vote type
        if (previousVote === 'upvote') {
          setUpvotes(prev => prev - 1)
          setDownvotes(prev => prev + 1)
        } else {
          setDownvotes(prev => prev - 1)
          setUpvotes(prev => prev + 1)
        }
      } else {
        // New vote
        if (type === 'upvote') {
          setUpvotes(prev => prev + 1)
        } else {
          setDownvotes(prev => prev + 1)
        }
      }
      
      onVote(type)
    }
  }

  const getScoreColor = () => {
    if (score > 0) return 'positive'
    if (score < 0) return 'negative'
    return 'neutral'
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
  }

  return (
    <div className={`vote-system ${orientation} ${size} ${isAnimating ? 'animating' : ''}`}>
      {/* Upvote Button */}
      <button
        onClick={() => handleVote('upvote')}
        className={`vote-btn upvote ${currentUserVote === 'upvote' ? 'active' : ''} ${!isLoggedIn ? 'disabled' : ''}`}
        disabled={!isLoggedIn}
        title={isLoggedIn ? (currentUserVote === 'upvote' ? 'Remove upvote' : 'Upvote') : 'Login to vote'}
        aria-label={`Upvote (${upvotes} upvotes)`}
      >
        <span className="vote-icon">▲</span>
        {orientation === 'horizontal' && (
          <span className="vote-count">{formatNumber(upvotes)}</span>
        )}
      </button>

      {/* Score Display */}
      {showScore && orientation === 'vertical' && (
        <div className={`vote-score ${getScoreColor()}`}>
          <span className="score-number">{formatNumber(score)}</span>
        </div>
      )}

      {/* Downvote Button */}
      <button
        onClick={() => handleVote('downvote')}
        className={`vote-btn downvote ${currentUserVote === 'downvote' ? 'active' : ''} ${!isLoggedIn ? 'disabled' : ''}`}
        disabled={!isLoggedIn}
        title={isLoggedIn ? (currentUserVote === 'downvote' ? 'Remove downvote' : 'Downvote') : 'Login to vote'}
        aria-label={`Downvote (${downvotes} downvotes)`}
      >
        <span className="vote-icon">▼</span>
        {orientation === 'horizontal' && (
          <span className="vote-count">{formatNumber(downvotes)}</span>
        )}
      </button>

      {/* Horizontal Score */}
      {showScore && orientation === 'horizontal' && (
        <div className={`vote-score horizontal ${getScoreColor()}`}>
          <span className="score-label">Score:</span>
          <span className="score-number">{formatNumber(score)}</span>
        </div>
      )}

      {/* Vote Percentage (for posts/threads with enough votes) */}
      {(upvotes + downvotes) >= 10 && (
        <div className="vote-percentage">
          <div className="percentage-bar">
            <div 
              className="percentage-fill"
              style={{ 
                width: `${(upvotes / (upvotes + downvotes)) * 100}%`,
                backgroundColor: 'var(--success-color, #00d166)'
              }}
            />
          </div>
          <span className="percentage-text">
            {Math.round((upvotes / (upvotes + downvotes)) * 100)}% upvoted
          </span>
        </div>
      )}
    </div>
  )
}